<template>
  <div class="cc-root">

    <div class="cc-bg">
      <div class="cc-bg__overlay"></div>
    </div>

    <div class="cc-page">
      <article class="cc-card">

        <div class="cc-card__bar" aria-hidden="true"></div>

        <div class="cc-card__inner">

          <img class="cc-logo cc-logo--dark" src="@/assets/CruzControl_Logo_Small_Icon.svg" alt="CruzControl" />
          <img class="cc-logo cc-logo--light" src="@/assets/CruzControl_Logo_Small_Icon_Light.svg" alt="CruzControl" />

          <header class="cc-header">
            <h1 class="cc-header__title">CHANGE PASSWORD</h1>
            <p class="cc-header__sub">Set a new password for your account</p>
          </header>

          <div v-if="submitting" class="cc-loading">
            <LoadingSpinner />
          </div>

          <div v-if="showError" class="cc-error" role="alert">
            <svg class="cc-error__icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <p class="cc-error__text animate-shake whitespace-pre break-all uppercase">{{ errorMsg }}</p>
          </div>

          <form @submit.prevent="login" class="cc-form" novalidate>

            <!-- Old password -->
            <div class="cc-field">
              <label class="cc-label" for="oldPassword">Old Password</label>
              <div class="cc-pw-row">
                <input
                  id="oldPassword"
                  name="oldPassword"
                  :type="showCurrent ? 'password' : 'text'"
                  required
                  v-model="oldPassword"
                  class="cc-input cc-input--pw"
                  placeholder="••••••••"
                />
                <button type="button" @click="showCurrent = !showCurrent" class="cc-eye">
                  <font-awesome-icon icon="fa-solid fa-eye" />
                </button>
              </div>
            </div>

            <!-- Password hints -->
            <div v-if='passwordValidation.errors.length > 0' class="cc-hint">
              <font-awesome-icon icon="fa-solid fa-info-circle" class="cc-hint__icon" />
              <div class="cc-hint__body">
                <p class="cc-hint__title">Password Requirements</p>
                <p class="cc-hint__item" v-for='error in passwordValidation.errors' :key="error">{{ error }}</p>
              </div>
            </div>

            <!-- New password -->
            <div class="cc-field">
              <label class="cc-label" for="newPassword">New Password</label>
              <div class="cc-pw-row">
                <input
                  id="newPassword"
                  name="newPassword"
                  :type="showNew ? 'password' : 'text'"
                  required
                  :disabled="!hasOldPassword"
                  v-model="newPassword"
                  class="cc-input cc-input--pw"
                  placeholder="••••••••"
                />
                <button type="button" @click="showNew = !showNew" :disabled="!oldPassword" class="cc-eye">
                  <font-awesome-icon icon="fa-solid fa-eye" />
                </button>
              </div>
            </div>

            <!-- Confirm password -->
            <div class="cc-field">
              <label class="cc-label" for="confirmPassword">Confirm Password</label>
              <div class="cc-pw-row">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  :type="showConfirm ? 'password' : 'text'"
                  required
                  :disabled="!newPassword"
                  v-model="confirmPassword"
                  v-on:keyup="validateConfirmPassword"
                  @blur.prevent="validateConfirmPassword"
                  class="cc-input cc-input--pw"
                  placeholder="••••••••"
                />
                <button type="button" @click="showConfirm = !showConfirm" :disabled="!newPassword" class="cc-eye">
                  <font-awesome-icon icon="fa-solid fa-eye" />
                </button>
              </div>
            </div>

            <button type="submit" :disabled="!canSubmit" class="cc-submit">
              <span>Submit</span>
              <span class="cc-submit__arrow" aria-hidden="true">→</span>
            </button>

          </form>
        </div>

        <div class="cc-card__foot">
          <span class="cc-card__wordmark">CRUZCONTROL</span>
        </div>

      </article>
    </div>

  </div>
</template>

<script>

import { signIn, signOut, confirmSignIn } from 'aws-amplify/auth';
import LoadingSpinner from "@/components/LoadingSpinner";
import {mapGetters, mapMutations} from "vuex";
import {store} from "@/store/store";
import {getUser} from "@/services/user.service";

export default {
  name: "FirstLoginPage",
  title: "Cruz Control | Login",
  components: {
    LoadingSpinner
  },
  data() {
    return {
      rules: [
        { message:'Contains at least 1 lowercase letter.', regex:/[a-z]+/ },
        { message:"Contains at least 1 uppercase letter.",  regex:/[A-Z]+/ },
        { message:"Contains at least 1 number.", regex:/[0-9]+/ },
        { message:"Contains at least 1 special character.", regex:/(?=.*[ -/:-@[-`{-~]{1,})+/ },
        { message:"8 characters minimum.", regex:/.{8,}/ }
      ],
      submitting: false,
      showError: false,
      errorMsg: "",
      noConnection: false,
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
      blurredNewPassword: false,
      blurredConfirmPassword: false,
      showCurrent: true,
      showNew: true,
      showConfirm: true,
    }
  },
  computed: {
    ...mapGetters(["getUser", "getEmail"]),
    passwordsFilled () {
      return (this.newPassword !== '' && this.confirmPassword !== '')
    },
    passwordValidation () {
      let errors = []
      for (let condition of this.rules) {
        if (!condition.regex.test(this.newPassword)) {
          errors.push(condition.message)
        }
      }
      if (errors.length === 0) {
        return { valid:true, errors }
      } else {
        return { valid:false, errors }
      }
    },
    hasOldPassword() {
      return this.oldPassword != ""
    },
    passwordsMatch() {
      return this.confirmPassword === this.newPassword
    },
    canSubmit(){
      return !this.submitting && this.oldPassword && this.passwordValidation.valid && this.passwordsFilled && this.passwordsMatch;
    }
  },
  methods: {
    ...mapMutations(["setUser"]),
    validateConfirmPassword () {
      if (!this.passwordsMatch && this.confirmPassword.length >= 8) {
        this.showError = true;
        this.errorMsg = "New And Confirm Password do not match"
      } else {
        this.showError = false;
      }
    },
    async login() {
      this.submitting = true
      this.showError = false
      this.errorMsg = ""

      try {
        // Clear any stale session so signIn doesn't throw "already signed in"
        try { await signOut(); } catch (_) { /* ignore if no session */ }

        const response = await signIn({ username: this.getEmail, password: this.oldPassword })

        // Only proceed if Cognito requires a new password
        if (response.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
          // Complete password reset through Cognito
          await confirmSignIn({ challengeResponse: this.newPassword })

          const userResponse = await getUser(this.getEmail)

          if (userResponse.length > 0) {
            const userData = userResponse[0]
            await store.commit('setUser', userData)
            await this.$router.push('/')
          } else {
            this.errorMsg = `ACCOUNT NOT FOUND`
            this.showError = true
          }
        } else {
          this.errorMsg = `PASSWORD ALREADY CHANGED. PLEASE SIGN IN NORMALLY.`
          this.showError = true
        }
      } catch (error) {
        console.log('First login error:', error)
        const msg = error?.message || error.toString()

        if (msg.includes("NotAuthorizedException")) {
          this.errorMsg = "INCORRECT OLD PASSWORD"
        } else if (msg.includes("InvalidPasswordException")) {
          // Extract the policy message if available
          const policyParts = msg.split('Password does not conform to policy: ')
          this.errorMsg = policyParts.length > 1 ? policyParts[1] : "NEW PASSWORD DOES NOT MEET REQUIREMENTS"
        } else if (msg.includes("TooManyRequestsException") || msg.includes("LimitExceededException")) {
          this.errorMsg = "TOO MANY ATTEMPTS. PLEASE TRY AGAIN LATER."
        } else if (msg.includes("UserNotConfirmedException")) {
          this.errorMsg = "ACCOUNT NOT CONFIRMED.\nPLEASE CHECK YOUR EMAIL FOR A VERIFICATION LINK."
        } else if (msg.includes("InvalidParameterException")) {
          this.errorMsg = "INVALID PASSWORD FORMAT"
        } else if (msg.includes("InternalErrorException")) {
          this.errorMsg = "AN INTERNAL ERROR OCCURRED. PLEASE TRY AGAIN."
        } else if (msg.includes("NetworkError") || msg.includes("Failed to fetch") || msg.includes("Network request failed")) {
          this.errorMsg = "UNABLE TO CONNECT. PLEASE CHECK YOUR INTERNET CONNECTION."
        } else {
          this.errorMsg = msg
        }
        this.showError = true
      } finally {
        this.submitting = false
      }
    }
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Big+Shoulders+Display:wght@700;800;900&family=Barlow:wght@300;400;500;600&display=swap');

.cc-root { position: fixed; inset: 0; overflow: hidden; font-family: 'Barlow', sans-serif; }
.cc-bg { position: absolute; inset: 0; z-index: 0; background-color: var(--cc-page); transition: background-color 0.3s; }
.cc-bg__overlay { position: absolute; inset: 0; background: var(--cc-overlay-grad); transition: background 0.3s; }
.cc-page { position: relative; z-index: 10; min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 1.5rem; }

.cc-card { width: 100%; max-width: 430px; background: var(--cc-card); backdrop-filter: blur(28px) saturate(150%); -webkit-backdrop-filter: blur(28px) saturate(150%); border: 1px solid var(--cc-card-border); border-radius: 4px; overflow: hidden; box-shadow: var(--cc-card-shadow); animation: cardIn 0.65s cubic-bezier(0.16, 1, 0.3, 1) both; transition: background 0.3s, border-color 0.3s, box-shadow 0.3s; }
@keyframes cardIn { from { opacity: 0; transform: translateY(22px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
.cc-card__bar { height: 3px; background: linear-gradient(90deg, #0092bf 0%, #00bcf5 55%, #fcef60 100%); }
.cc-card__inner { padding: 2.25rem 2.5rem 0; }

.cc-logo { height: 42px; width: auto; display: block; margin-bottom: 1.75rem; }
.cc-logo--light { display: none; }
[data-theme="light"] .cc-logo--dark  { display: none; }
[data-theme="light"] .cc-logo--light { display: block; }

.cc-header { margin-bottom: 1.75rem; }
.cc-header__title { font-family: 'Big Shoulders Display', sans-serif; font-size: 3rem; font-weight: 900; letter-spacing: 0.04em; color: var(--cc-t1); line-height: 1; margin: 0 0 0.4rem; transition: color 0.3s; }
.cc-header__sub { font-size: 0.8125rem; font-weight: 300; color: var(--cc-t3); letter-spacing: 0.02em; margin: 0; transition: color 0.3s; }

.cc-loading { padding: 1.5rem 0 0.5rem; display: flex; justify-content: center; }

.cc-error { display: flex; align-items: flex-start; gap: 0.625rem; background: rgba(239,68,68,0.10); border-left: 3px solid #EF4444; padding: 0.75rem 1rem; border-radius: 0 2px 2px 0; margin-bottom: 1.25rem; }
.cc-error__icon { flex-shrink: 0; width: 16px; height: 16px; color: #f87171; margin-top: 2px; }
.cc-error__text { font-size: 0.8125rem; color: #fca5a5; font-weight: 500; margin: 0; line-height: 1.55; }

.cc-hint { display: flex; align-items: flex-start; gap: 0.625rem; background: rgba(0,146,191,0.08); border-left: 3px solid #0092bf; padding: 0.75rem 1rem; border-radius: 0 2px 2px 0; }
.cc-hint__icon { flex-shrink: 0; font-size: 0.875rem; color: #0092bf; margin-top: 2px; }
.cc-hint__body { flex: 1; }
.cc-hint__title { font-size: 0.6875rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: var(--cc-t2); margin: 0 0 0.25rem; }
.cc-hint__item { font-size: 0.75rem; color: var(--cc-t2); margin: 0; line-height: 1.6; }

.cc-form { display: flex; flex-direction: column; gap: 1.25rem; }
.cc-field { display: flex; flex-direction: column; gap: 0.4rem; }
.cc-label { display: block; font-size: 0.6875rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.09em; color: var(--cc-t4); transition: color 0.3s; }

.cc-input { width: 100%; box-sizing: border-box; background: var(--cc-input); border: 1px solid var(--cc-input-border); border-radius: 3px; padding: 0.75rem 1rem; font-family: 'Barlow', sans-serif; font-size: 0.9375rem; font-weight: 400; color: var(--cc-input-text); outline: none; transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, color 0.2s ease; }
.cc-input::placeholder { color: var(--cc-placeholder); }
.cc-input:focus { border-color: #0092bf; background: rgba(0,146,191,0.07); box-shadow: 0 0 0 3px rgba(0,146,191,0.14); }
.cc-input:disabled { opacity: 0.4; cursor: not-allowed; }
.cc-input:-webkit-autofill, .cc-input:-webkit-autofill:hover, .cc-input:-webkit-autofill:focus { -webkit-box-shadow: 0 0 0 1000px var(--cc-autofill-bg) inset; -webkit-text-fill-color: var(--cc-input-text); border-color: var(--cc-input-border); caret-color: var(--cc-input-text); }

.cc-pw-row { display: flex; }
.cc-input--pw { border-radius: 3px 0 0 3px; flex: 1; min-width: 0; }
.cc-eye { flex-shrink: 0; background: var(--cc-eye-bg); border: 1px solid var(--cc-eye-border); border-left: none; border-radius: 0 3px 3px 0; padding: 0 1rem; color: var(--cc-eye-icon); cursor: pointer; display: flex; align-items: center; font-size: 0.875rem; transition: background 0.18s, color 0.18s; }
.cc-eye:hover { background: var(--cc-toggle-hover); color: var(--cc-t1); }
.cc-eye:focus { outline: none; box-shadow: 0 0 0 3px rgba(0,146,191,0.14); }
.cc-eye:disabled { opacity: 0.35; cursor: not-allowed; }

.cc-submit { display: flex; align-items: center; justify-content: center; gap: 0.5rem; width: 100%; background: #0092bf; border: none; border-radius: 3px; padding: 0.875rem 1rem; font-family: 'Barlow', sans-serif; font-size: 0.875rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.10em; color: #ffffff; cursor: pointer; position: relative; overflow: hidden; text-decoration: none; transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease; margin-top: 0.25rem; }
.cc-submit::after { content: ''; position: absolute; inset: 0; background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.10) 50%, transparent 100%); transform: translateX(-100%); transition: transform 0.45s ease; }
.cc-submit:hover::after { transform: translateX(100%); }
.cc-submit:hover { background: #00a8d8; box-shadow: 0 6px 28px rgba(0,146,191,0.42); }
.cc-submit:active { transform: scale(0.99); background: #007da3; }
.cc-submit:disabled { background: rgba(255,255,255,0.1); cursor: not-allowed; box-shadow: none; }
.cc-submit:disabled::after { display: none; }
.cc-submit__arrow { font-size: 1rem; display: inline-block; transition: transform 0.2s ease; }
.cc-submit:hover:not(:disabled) .cc-submit__arrow { transform: translateX(4px); }

.cc-card__foot { margin-top: 2rem; padding: 1rem 2.5rem; border-top: 1px solid var(--cc-card-foot); display: flex; justify-content: center; transition: border-color 0.3s; }
.cc-card__wordmark { font-family: 'Big Shoulders Display', sans-serif; font-size: 0.6875rem; font-weight: 700; letter-spacing: 0.32em; color: var(--cc-t5); user-select: none; transition: color 0.3s; }
</style>